import { Box, IconButton, keyframes, styled, useTheme } from '@mui/material';
import {
  LoadingIndicator,
  Typography,
} from '@forethought-technologies/forethought-elements';

import {
  DailyAudio,
  DailyProvider,
  useCallObject,
  useDaily,
  useDailyEvent,
} from '@daily-co/daily-react';

import { IconPhone, IconX } from '@tabler/icons-react';
import { useCallback, useState } from 'react';
import type { ZendeskSIPCallStepComponent } from 'api/types';
import { PopupModal } from 'components/PopupModal';
import { addEventMessage } from 'reducers/workflows';
import { useAppDispatch } from 'hooks';
import { formatDuration, useDuration } from 'components/App/hooks/useDuration';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';

interface SIPCallStepProps {
  isLastStep: boolean;
  setStepSent: (query?: string) => void;
  stepIndex: number;
  widgetComponent: ZendeskSIPCallStepComponent;
}

export const ZendeskSipCallStep = ({
  setStepSent,
  widgetComponent,
}: SIPCallStepProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const { component_fields } = widgetComponent;
  const { room_url: roomUrl, sip_uri: sipUrl, token: token } = component_fields;
  const callObject = useCallObject({
    shouldCreateInstance: () => !widgetComponent.isSent,
  });

  const isSent = !!widgetComponent.isSent;

  const onCallEnd = useCallback(() => {
    setIsOpen(false);
    setStepSent();
    if (callObject) {
      callObject.leave()?.then(() => {
        return callObject.destroy();
      });
    }
  }, [callObject, setStepSent]);

  if (isSent) {
    return null; // Don't render if the step has already been used to make a call
  }

  return (
    <PopupModal open={isOpen} position='bottom'>
      <DailyProvider callObject={callObject}>
        <SipCallComponent
          meetingToken={token}
          onCallEnd={onCallEnd}
          roomUrl={roomUrl}
          sipUri={sipUrl}
        />
      </DailyProvider>
    </PopupModal>
  );
};

const CallDuration = ({ duration }: { duration: number }) => {
  return (
    <Box sx={{ visibility: !duration ? 'hidden' : 'visible' }}>
      <Typography variant='font14'>{formatDuration(duration)}</Typography>
    </Box>
  );
};

const SipCallComponent = ({
  meetingToken,
  onCallEnd,
  roomUrl,
  sipUri,
}: {
  meetingToken: string;
  onCallEnd: () => void;
  roomUrl: string;
  sipUri: string;
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const callObject = useDaily();
  // @ts-expect-error add callObject to window for debugging
  window.callObject = callObject;
  const [dialOutSessionId, setDialOutSessionId] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const duration = useDuration(isConnected);

  const end = useCallback(
    ({ addWidgetComponent = true }: { addWidgetComponent: boolean }) => {
      onCallEnd();
      if (addWidgetComponent) {
        if (dialOutSessionId) {
          dispatch(addEventMessage(`Call ended (${formatDuration(duration)})`));
        } else {
          dispatch(addEventMessage('Call canceled'));
        }
      }
    },
    [dialOutSessionId, dispatch, duration, onCallEnd],
  );

  const leaveRoom = useCallback(() => {
    if (!callObject) {
      return;
    }
    callObject
      .leave()
      ?.then(() => {
        return callObject.destroy();
      })
      .catch((err: unknown) => {
        sendErrorToSentry({
          consoleError: true,
          error: `Error leaving room: ${err}`,
        });
      });
  }, [callObject]);

  // Start dial-out to SIP URI
  const startDialOut = useCallback(() => {
    setIsConnecting(true);
    if (!callObject) {
      return;
    }

    callObject
      .join({
        startVideoOff: true,
        token: meetingToken,
        url: roomUrl,
      })
      .then(() => {
        callObject
          .startDialOut({
            displayName: 'SIP User',
            sipUri: sipUri,
            video: true,
          })
          .then(result => {
            setIsConnecting(false);
            setIsConnected(true);
            // Track the session ID if available in the result
            if (typeof result === 'object' && result?.session?.sessionId) {
              setDialOutSessionId(result.session.sessionId);
            }
          })
          .catch((err: unknown) => {
            sendErrorToSentry({
              consoleError: true,
              error: `Error starting dial-out: ${err}`,
            });
          });
      })
      .catch((err: unknown) => {
        sendErrorToSentry({
          consoleError: true,
          error: `Error joining room: ${err}`,
        });
      });
  }, [callObject, meetingToken, roomUrl, sipUri]);

  // Stop dial-out
  const stopDialOut = useCallback(() => {
    if (!callObject) {
      return;
    }

    if (!dialOutSessionId) {
      return;
    }

    callObject
      .stopDialOut({ sessionId: dialOutSessionId })
      .catch((err: unknown) => {
        sendErrorToSentry({
          additionalContext: { sessionId: dialOutSessionId },
          consoleError: true,
          error: `Error stopping dial-out: ${err}`,
        });
      })
      .finally(() => {
        end({ addWidgetComponent: true });
      });
  }, [callObject, dialOutSessionId, end]);

  const cleanupEndCall = useCallback(
    (e: { action?: string } | undefined) => {
      setIsConnected(false);
      setIsConnecting(false);
      leaveRoom();
      end({ addWidgetComponent: e?.action !== 'left-meeting' });
    },
    [leaveRoom, end],
  );

  useDailyEvent('left-meeting', cleanupEndCall);
  useDailyEvent('dialout-stopped', cleanupEndCall);

  return (
    <>
      <CallInterfaceContainer>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
          }}
        >
          <Typography variant='font24'>Call an agent</Typography>
          <IconButton
            onClick={() => {
              cleanupEndCall(undefined);
            }}
            sx={{ position: 'absolute', right: '16px' }}
          >
            <IconX size={20} />
          </IconButton>
        </Box>
        <ButtonWrapper>
          <CallDuration duration={duration} />
          <CallButton
            disabled={!sipUri.length || isConnecting}
            isCalling={isConnected}
            onClick={() => {
              isConnected ? stopDialOut() : startDialOut();
            }}
          >
            {isConnecting ? (
              <LoadingIndicator color={theme.palette.colors.white} />
            ) : (
              <IconPhone
                color='white'
                size={40}
                style={{
                  transform: isConnected ? 'rotate(135deg)' : undefined,
                }}
              />
            )}
          </CallButton>
        </ButtonWrapper>
        <Box textAlign='center'>
          <Typography color={theme.palette.colors.grey[600]} variant='font14'>
            Please note that if you leave this page or refresh, the call would
            be disconnected.
          </Typography>
        </Box>
      </CallInterfaceContainer>
      <DailyAudio />
    </>
  );
};

const CustomizedIconButton = styled('button')`
  cursor: pointer;
  display: flex;
  width: 80px;
  height: 80px;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  flex-shrink: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid ${props => props.theme.palette.colors.slate[100]};

  &:hover {
    background: rgba(255, 255, 255, 0.4);
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.03); }
`;

const CallButton = styled(CustomizedIconButton, {
  shouldForwardProp: prop => prop !== 'isCalling',
})<{ isCalling: boolean }>(({ isCalling, theme }) => ({
  '&:hover': {
    backgroundColor: isCalling
      ? theme.palette.colors.red[400]
      : theme.palette.colors.green[400],
  },

  animation: isCalling ? 'none' : `${pulse} 2s ease-in-out infinite`,

  backgroundColor: isCalling
    ? theme.palette.colors.red[500]
    : theme.palette.colors.green[500],
}));

const CallInterfaceContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
`;

const ButtonWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
  align-items: center;
  width: 80%;
`;
